import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { IoIosClose } from "react-icons/io";
const ArrivalTimePicker = ({
	date,
	isModalOpen,
	setIsModalOpen,
	setRequestSent,
	setTimeOnly,
	currentSelectedTime,
	propertyEarlyCheckInTime,
	propertyTime,
	propertyTimeZone,
	addon,
}) => {
	console.log(
		"DateandTimePicker rendered with date:",
		propertyEarlyCheckInTime,
	);
	const [selectedDate, setSelectedDate] = useState(null);
	const [selectedTime, setSelectedTime] = useState("");
	const [timeSlots, setTimeSlots] = useState([]);
	const [isEarlyCheckIn, setIsEarlyCheckIn] = useState(false);
	const [showEarlyCheckInModal, setShowEarlyCheckInModal] = useState(false);
	const [tempSelectedTime, setTempSelectedTime] = useState("");
	const timeSlotsRef = useRef(null);

	// Convert 24hr to 12hr format
	const convert24to12 = (time24) => {
		return moment(time24, "HH:mm").format("hh:mm A");
	};

	// Check if selected time is 15 minutes or more before standard check-in time
	const checkIfEarlyCheckIn = (selectedTimeStr, standardCheckInTime) => {
		if (!selectedTimeStr || !standardCheckInTime) return false;
		
		const selected = moment(selectedTimeStr, "hh:mm A");
		const standard = moment(standardCheckInTime, "HH:mm");
		
		// If selected time is exactly the standard check-in time, it's not early check-in
		if (selected.isSame(standard)) return false;
		
		// Calculate difference in minutes
		const diffMinutes = standard.diff(selected, 'minutes');
		
		// Return true if selected time is 15 minutes or more before standard time
		return diffMinutes >= 15;
	};

	const generateTimeSlots = (start, end, intervalMinutes) => {
		const slots = [];
		let current = new Date(start);
		const endDate = new Date(end);

		while (current <= endDate) {
			// Convert directly to 12-hour format
			const time12 = moment(current).format("hh:mm A");
			slots.push(time12);
			current.setMinutes(current.getMinutes() + intervalMinutes);
		}
		console.log(slots);
		return slots;
	};

	useEffect(() => {
		const now = new Date();
		// Convert propertyTime from 24hr to 12hr format
		const propertyTime12 = convert24to12(propertyTime || "00:00");
		const [propertyHour, propertyMinute] = moment(propertyTime12, "hh:mm A")
			.format("HH:mm")
			.split(":")
			.map(Number);

		// Start time: 15 minutes before standard check-in time
		const startTime = new Date();
		startTime.setHours(propertyHour, propertyMinute - 15, 0);

		// End time: 11:45 PM
		const endTime = new Date();
		endTime.setHours(23, 45, 0); // 11:45 PM

		// Generate time slots from start time to end time with 15-minute intervals
		const slots = generateTimeSlots(startTime, endTime, 15);

		// Include the standard check-in time in the slots if it's not already there
		if (!slots.includes(propertyTime12)) {
			slots.push(propertyTime12);
		}

		// Sort the slots chronologically
		slots.sort((a, b) => {
			const timeA = moment(a, "hh:mm A");
			const timeB = moment(b, "hh:mm A");
			return timeA.diff(timeB);
		});

		setTimeSlots(slots);
		// Set current selected time as default, or standard check-in time as fallback
		const defaultTime = currentSelectedTime || propertyTime12;
		setSelectedTime(defaultTime);

		console.log("Time Slots: ", slots);
	}, [propertyTime, propertyEarlyCheckInTime, currentSelectedTime]);

	// Check if selected time is early check-in whenever selectedTime changes
	useEffect(() => {
		const isEarly = checkIfEarlyCheckIn(selectedTime, propertyTime);
		setIsEarlyCheckIn(isEarly);
	}, [selectedTime, propertyTime]);

	// Handle time selection
	const handleTimeSelection = (timeSlot) => {
		const isEarly = checkIfEarlyCheckIn(timeSlot, propertyTime);
		
		if (isEarly) {
			// Show modal for early check-in
			setTempSelectedTime(timeSlot);
			setShowEarlyCheckInModal(true);
		} else {
			// For standard/late check-in, just update the time without sending request
			setSelectedTime(timeSlot);
			// Update the arrival time input field without sending request
			if (setTimeOnly) {
				setTimeOnly(timeSlot);
			}
			// Close the modal
			setIsModalOpen(false);
		}
	};

	// Handle early check-in confirmation
	const handleEarlyCheckInConfirm = () => {
		setSelectedTime(tempSelectedTime);
		setShowEarlyCheckInModal(false);
		setTempSelectedTime("");
		// Send the early check-in request and close the main modal
		setRequestSent(tempSelectedTime);
		setIsModalOpen(false);
	};

	// Handle early check-in cancellation
	const handleEarlyCheckInCancel = () => {
		setShowEarlyCheckInModal(false);
		setTempSelectedTime("");
	};

	// Scroll to selected time when modal opens
	const scrollToSelectedTime = () => {
		if (timeSlotsRef.current && selectedTime) {
			const selectedElement = timeSlotsRef.current.querySelector(`[data-time="${selectedTime}"]`);
			if (selectedElement) {
				selectedElement.scrollIntoView({
					behavior: 'smooth',
					block: 'center'
				});
			}
		}
	};

	// Scroll to selected time when modal opens
	useEffect(() => {
		if (isModalOpen && selectedTime) {
			// Small delay to ensure DOM is rendered
			setTimeout(() => {
				scrollToSelectedTime();
			}, 100);
		}
	}, [isModalOpen, selectedTime]);

	return (
		<div
			className={`fixed bottom-0 left-0 w-full bg-white p-4 shadow-lg transition-transform transform ${
				isModalOpen ? "translate-y-0" : "translate-y-full"
			} h-80`}
			style={{ zIndex: 9999 }}
		>
			<div className="flex items-center justify-between pb-4">
				<div className="text-lg text-black">Early check-in time</div>
				<button
					onClick={(e) => {
						e.preventDefault();
						setIsModalOpen(false);
					}}
				>
					<IoIosClose className="h-8 w-8 text-black" />
				</button>
			</div>

			<div className="mb-4 text-lg text-center">
				<div className="text-left text-black mb-3">
					Select Time:
				</div>
				{timeSlots.length > 0 ? (
					<div ref={timeSlotsRef} className="max-h-32 overflow-y-auto">
						{timeSlots.map((slot, index) => (
							<div
								key={index}
								data-time={slot}
								onClick={() => handleTimeSelection(slot)}
								className={`w-full px-4 py-2 cursor-pointer text-black border-b border-gray-200 hover:bg-gray-50 ${
									selectedTime === slot ? 'bg-indigo-50 text-indigo-600' : ''
								}`}
							>
								{slot}
							</div>
						))}
					</div>
				) : (
					<div className="text-black">No available time slots</div>
				)}
			</div>

			{/* Only show Send Request button for early check-in times */}
			{isEarlyCheckIn && selectedTime && (
				<button
					className="w-full py-2 mt-2 border border-indigo-600 text-indigo-600 rounded-md hover:bg-indigo-50"
					onClick={(e) => {
						e.preventDefault();
						setRequestSent(selectedTime); // Sending 12hr format to parent
						setIsModalOpen(false);
					}}
				>
					Send Request {addon && addon.price ? `($${addon?.price})` : ""}
				</button>
			)}

			{/* Early Check-in Notification Modal */}
			{showEarlyCheckInModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg p-6 mx-4 max-w-sm w-full">
						<div className="text-center">
							<div className="text-lg font-semibold text-gray-800 mb-4">
								Early Check-in Request
							</div>
							<div className="text-gray-600 mb-6">
								As you want to check in early, it will be requested to the front desk and can also charge you ${addon?.price || 'additional fee'}. Your request will be updated as soon as possible.
							</div>
							<div className="flex space-x-3">
								<button
									onClick={handleEarlyCheckInCancel}
									className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
								>
									Cancel
								</button>
								<button
									onClick={handleEarlyCheckInConfirm}
									className="flex-1 py-2 px-4 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
								>
									Send Request
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default ArrivalTimePicker;
